import {
  Controller as TabsController,
  ListingPageHeader,
  ModalTab,
} from 'components/Shared';
import type {
  PortariaUser,
  PortariaUserPayload,
} from 'contracts/PortariaUsers';
import type { YardRole, YardRolePayload } from 'contracts/YardRoles';
import { usePortariaAccess } from 'hooks/api/usePortariaAccess';
import { useAuth } from 'hooks';
import { Scaffold } from 'layouts';
import { Check, Plus, Search, Trash2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { apiErrorHandler, notify } from 'services';
import { Button } from 'styles/components';
import * as S from './styles';
import {
  isStrongPassword,
  PASSWORD_RULES,
  passwordsMatch,
} from './passwordPolicy';
import { PasswordInput } from './PasswordInput';

const sanitizeName = (value: string) => value.replace(/[^a-zA-ZÀ-ÿ0-9_ ]/g, '');

const normalizeCode = (value: string) =>
  sanitizeName(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ /g, '_')
    .toUpperCase()
    .slice(0, 50);

const emptyUserDraft = () => ({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  blocked: false,
});

const emptyRoleDraft = (): YardRolePayload => ({
  code: '',
  name: '',
  description: '',
  active: true,
});

export const PortariaAccessPage: React.FC = () => {
  const {
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    getRoles,
    createRole,
    updateRole,
    deleteRole,
  } = usePortariaAccess();

  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState<PortariaUser[]>([]);
  const [roles, setRoles] = useState<YardRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { profile } = useAuth();
  const canCreateUsers = Boolean(profile?.root);

  // users
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [userDraft, setUserDraft] = useState(emptyUserDraft);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userErrors, setUserErrors] = useState<Record<string, string>>({});
  const [isSavingUser, setIsSavingUser] = useState(false);

  // roles
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [roleDraft, setRoleDraft] = useState<YardRolePayload>(emptyRoleDraft);
  const [roleSearch, setRoleSearch] = useState('');
  const [roleErrors, setRoleErrors] = useState<Record<string, string>>({});
  const [isSavingRole, setIsSavingRole] = useState(false);

  const isNameValid = userDraft.name.trim() !== '';
  const isEmailValid = /^\S+@\S+\.\S+$/.test(userDraft.email.trim());
  const passwordStrength = isStrongPassword(userDraft.password);
  const passwordMatches = passwordsMatch(
    userDraft.password,
    userDraft.confirmPassword
  );
  const isPasswordChangeRequested =
    userDraft.password !== '' || userDraft.confirmPassword !== '';
  const shouldValidatePassword = isCreatingUser || isPasswordChangeRequested;
  const isPasswordValid =
    !shouldValidatePassword || (passwordStrength && passwordMatches);
  const confirmMismatch =
    shouldValidatePassword &&
    userDraft.confirmPassword !== '' &&
    !passwordMatches;
  const canSaveUser = isNameValid && isEmailValid && isPasswordValid;

  useEffect(() => {
    let mounted = true;

    Promise.all([getUsers({ limit: 100 }), getRoles()])
      .then(([loadedUsers, loadedRoles]) => {
        if (!mounted) return;
        setUsers(loadedUsers.data);
        setRoles(loadedRoles);
      })
      .catch((error) => notify('error', apiErrorHandler(error).errorMessage))
      .finally(() => mounted && setIsLoading(false));

    return () => {
      mounted = false;
    };
  }, [getRoles, getUsers]);

  const visibleUsers = useMemo(
    () =>
      users.filter((user) =>
        `${user.name} ${user.email}`
          .toLocaleLowerCase()
          .includes(userSearch.toLocaleLowerCase())
      ),
    [userSearch, users]
  );

  const visibleRoles = useMemo(
    () =>
      roles.filter((role) =>
        `${role.code} ${role.name}`
          .toLocaleLowerCase()
          .includes(roleSearch.toLocaleLowerCase())
      ),
    [roleSearch, roles]
  );

  const clearUserError = (field: string) => {
    setUserErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const selectUser = (user: PortariaUser) => {
    setSelectedUserId(user.id);
    setIsCreatingUser(false);
    setUserDraft({
      name: user.name,
      email: user.email,
      password: '',
      confirmPassword: '',
      blocked: Boolean(user.blockedAt),
    });
    setSelectedRoleIds(user.roles.map((role) => role.id));
    setUserErrors({});
  };

  const startNewUser = () => {
    if (!canCreateUsers) return;
    setSelectedUserId(null);
    setIsCreatingUser(true);
    setUserDraft(emptyUserDraft());
    setSelectedRoleIds([]);
    setUserErrors({});
  };

  const updateUserDraft = <K extends keyof ReturnType<typeof emptyUserDraft>>(
    field: K,
    value: ReturnType<typeof emptyUserDraft>[K]
  ) => {
    setUserDraft((current) => ({ ...current, [field]: value }));
    clearUserError(field);
  };

  const toggleRole = (roleId: number) => {
    setSelectedRoleIds((current) =>
      current.includes(roleId)
        ? current.filter((id) => id !== roleId)
        : [...current, roleId]
    );
  };

  const saveUser = async () => {
    const nextErrors: Record<string, string> = {};
    if (!userDraft.name.trim()) nextErrors.name = 'Informe o nome do usuário.';
    if (!/^\S+@\S+\.\S+$/.test(userDraft.email.trim()))
      nextErrors.email = 'Informe um e-mail válido.';
    if (shouldValidatePassword && !userDraft.password) {
      nextErrors.password = 'Informe uma senha.';
    } else if (shouldValidatePassword && !passwordStrength) {
      nextErrors.password = 'A senha não atende aos requisitos de senha forte.';
    }
    if (shouldValidatePassword && !userDraft.confirmPassword) {
      nextErrors.confirmPassword = 'Confirme a senha.';
    } else if (shouldValidatePassword && !passwordMatches) {
      nextErrors.confirmPassword = 'As senhas não coincidem.';
    }
    setUserErrors(nextErrors);
    if (Object.keys(nextErrors).length || isSavingUser) return;

    setIsSavingUser(true);
    try {
      const payload: PortariaUserPayload = {
        ...(selectedUserId ? { userId: selectedUserId } : {}),
        user: {
          name: userDraft.name.trim(),
          email: userDraft.email.trim(),
          ...(userDraft.password !== '' ? { password: userDraft.password } : {}),
          blockedAt: userDraft.blocked ? new Date().toISOString() : null,
        },
        roleIds: selectedRoleIds,
      };

      if (isCreatingUser) {
        await createUser(payload);
      } else if (selectedUserId) {
        await updateUser(selectedUserId, payload);
      }

      const loaded = await getUsers({ limit: 100 });
      setUsers(loaded.data);
      setIsCreatingUser(false);
      setSelectedUserId(null);
      setUserDraft(emptyUserDraft());
      setSelectedRoleIds([]);
      notify('success', 'Usuário salvo com sucesso');
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUserId || isSavingUser) return;
    setIsSavingUser(true);
    try {
      await deleteUser(selectedUserId);
      const loaded = await getUsers({ limit: 100 });
      setUsers(loaded.data);
      setSelectedUserId(null);
      setIsCreatingUser(false);
      setUserDraft(emptyUserDraft());
      setSelectedRoleIds([]);
      notify('success', 'Usuário excluído com sucesso');
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSavingUser(false);
    }
  };

  const selectRole = (role: YardRole) => {
    setSelectedRoleId(role.id);
    setIsCreatingRole(false);
    setRoleDraft({
      code: role.code,
      name: role.name,
      description: role.description ?? '',
      active: role.active,
    });
    setRoleErrors({});
  };

  const startNewRole = () => {
    setSelectedRoleId(null);
    setIsCreatingRole(true);
    setRoleDraft(emptyRoleDraft());
    setRoleErrors({});
  };

  const updateRoleDraft = <K extends keyof YardRolePayload>(
    field: K,
    value: YardRolePayload[K]
  ) => {
    setRoleDraft((current) => ({ ...current, [field]: value }));
    setRoleErrors((current) => ({ ...current, [field]: '' }));
  };

  const saveRole = async () => {
    const nextErrors: Record<string, string> = {};
    if (!roleDraft.name.trim()) nextErrors.name = 'Informe o nome do grupo.';
    if (!roleDraft.code.trim()) nextErrors.code = 'Informe o código do grupo.';
    setRoleErrors(nextErrors);
    if (Object.keys(nextErrors).length || isSavingRole) return;

    setIsSavingRole(true);
    try {
      const payload = {
        ...roleDraft,
        code: roleDraft.code.trim(),
        name: roleDraft.name.trim(),
        description: roleDraft.description?.trim() || null,
      };
      const savedRole = selectedRoleId
        ? await updateRole(selectedRoleId, payload)
        : await createRole(payload);
      const loaded = await getRoles();
      setRoles(loaded);
      selectRole(savedRole);
      setIsCreatingRole(false);
      notify('success', 'Grupo salvo com sucesso');
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRoleId || isSavingRole) return;
    setIsSavingRole(true);
    try {
      await deleteRole(selectedRoleId);
      const loaded = await getRoles();
      setRoles(loaded);
      setSelectedRoleId(null);
      setIsCreatingRole(false);
      setRoleDraft(emptyRoleDraft());
      notify('success', 'Grupo excluído com sucesso');
    } catch (error) {
      notify('error', apiErrorHandler(error).errorMessage);
    } finally {
      setIsSavingRole(false);
    }
  };

  return (
    <Scaffold>
      <S.MainPanel className="min-h-full">
        <ListingPageHeader
          icon={<S.SettingsIcon />}
          title="Controle de acesso"
          isLoading={isLoading || isSavingUser || isSavingRole}
        />
        <main className="flex w-full flex-1 flex-col overflow-hidden">
          <TabsController onTabChange={setActiveTab}>
            {[
              <ModalTab key="users" title="Usuários" />,
              <ModalTab key="roles" title="Grupos" />,
            ]}
          </TabsController>
          {activeTab === 0 ? (
            <div className="flex min-h-[calc(100vh-205px)] flex-1 overflow-hidden">
              <aside className="flex w-72 shrink-0 flex-col border-r border-slate-200 bg-white">
                <div className="border-b border-slate-200 p-3">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-base font-bold text-slate-900">
                      Usuários
                    </h2>
                    {canCreateUsers && (
                      <button
                        aria-label="Novo usuário da Portaria"
                        className="flex h-7 w-7 items-center justify-center rounded bg-[#EE7C2C] text-white"
                        onClick={startNewUser}
                        type="button"
                      >
                        <Plus size={16} />
                      </button>
                    )}
                  </div>
                  {!canCreateUsers && (
                    <p className="mb-2 text-xs text-slate-500">
                      Somente administradores raiz podem criar usuários da
                      Portaria.
                    </p>
                  )}
                  <div className="relative">
                    <Search
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                      size={15}
                    />
                    <input
                      aria-label="Filtrar usuários"
                      className="w-full rounded border border-slate-300 py-1.5 pl-8 pr-2 text-sm outline-none focus:border-[#EE7C2C]"
                      placeholder="Filtrar..."
                      value={userSearch}
                      onChange={(event) => setUserSearch(event.target.value)}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {visibleUsers.map((user) => (
                    <button
                      aria-pressed={user.id === selectedUserId}
                      className={`relative block w-full border-b border-slate-200 p-3 text-left transition-colors ${
                        user.id === selectedUserId
                          ? 'bg-orange-50'
                          : 'hover:bg-slate-50'
                      }`}
                      key={user.id}
                      onClick={() => selectUser(user)}
                      type="button"
                    >
                      {user.id === selectedUserId && (
                        <span className="absolute bottom-0 left-0 top-0 w-1 bg-[#EE7C2C]" />
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <strong className="truncate text-sm text-slate-800">
                          {user.name}
                        </strong>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${
                            user.blockedAt
                              ? 'bg-red-100 text-red-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {user.blockedAt ? 'BLOQUEADO' : 'ATIVO'}
                        </span>
                      </div>
                      <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                        {user.email}
                      </span>
                      <span className="mt-0.5 block truncate text-[11px] font-semibold text-slate-400">
                        {user.roles.map((role) => role.name).join(', ')}
                      </span>
                    </button>
                  ))}
                  {!visibleUsers.length && (
                    <p className="p-4 text-center text-xs text-slate-500">
                      Nenhum usuário encontrado.
                    </p>
                  )}
                </div>
              </aside>

              <section className="min-w-0 flex-1 overflow-y-auto p-6">
                {!isCreatingUser && !selectedUserId ? (
                  <div className="flex h-full items-center justify-center">
                    <p className="rounded border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">
                      Selecione um usuário para editar ou clique em + para
                      cadastrar um novo.
                    </p>
                  </div>
                ) : (
                  <div className="mx-auto max-w-4xl rounded border border-slate-200 bg-white shadow-sm">
                    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">
                          {isCreatingUser ? 'Novo usuário' : 'Editar usuário'}
                        </h2>
                        <p className="mt-1 text-sm font-semibold text-slate-500">
                          Usuários com acesso exclusivo ao aplicativo da
                          Portaria.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isCreatingUser && selectedUserId && (
                          <Button
                            disabled={isSavingUser}
                            mood="light"
                            onClick={handleDeleteUser}
                            size="small"
                            type="button"
                          >
                            <Trash2 size={16} /> Excluir
                          </Button>
                        )}
                        <Button
                          disabled={isSavingUser || !canSaveUser}
                          mood="primary"
                          onClick={saveUser}
                          size="small"
                          type="button"
                        >
                          <Check size={16} />{' '}
                          {isSavingUser ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </div>
                    </header>
                    <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
                      <label className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Nome
                        </span>
                        <input
                          aria-label="Nome do usuário"
                          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#EE7C2C]"
                          maxLength={255}
                          value={userDraft.name}
                          onChange={(event) =>
                            updateUserDraft('name', event.target.value)
                          }
                        />
                        {userErrors.name && (
                          <span className="text-xs font-bold italic text-red-600">
                            {userErrors.name}
                          </span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          E-mail
                        </span>
                        <input
                          aria-label="E-mail do usuário"
                          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#EE7C2C]"
                          maxLength={255}
                          value={userDraft.email}
                          onChange={(event) =>
                            updateUserDraft('email', event.target.value)
                          }
                        />
                        {userErrors.email && (
                          <span className="text-xs font-bold italic text-red-600">
                            {userErrors.email}
                          </span>
                        )}
                      </label>
                      {!isCreatingUser && (
                        <>
                          <label className="flex flex-col gap-1">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                              Nova senha
                            </span>
                            <PasswordInput
                              ariaLabel={'Nova senha do usu\u00e1rio'}
                              value={userDraft.password}
                              onChange={(value) =>
                                updateUserDraft('password', value)
                              }
                            />
                            {userErrors.password && (
                              <span className="text-xs font-bold italic text-red-600">
                                {userErrors.password}
                              </span>
                            )}
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                              Confirmar nova senha
                            </span>
                            <PasswordInput
                              ariaLabel={'Confirmar nova senha do usu\u00e1rio'}
                              invalid={confirmMismatch}
                              value={userDraft.confirmPassword}
                              onChange={(value) =>
                                updateUserDraft('confirmPassword', value)
                              }
                            />
                            {(userErrors.confirmPassword ||
                              confirmMismatch) && (
                              <span className="text-xs font-bold italic text-red-600">
                                {userErrors.confirmPassword ||
                                  'As senhas n\u00e3o coincidem.'}
                              </span>
                            )}
                          </label>
                          {shouldValidatePassword && (
                            <div className="flex flex-col gap-1.5 md:col-span-2">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                Requisitos de senha forte
                              </span>
                              <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                                {PASSWORD_RULES.map((rule) => {
                                  const met = rule.test(userDraft.password);
                                  return (
                                    <li
                                      className={`flex items-center gap-1.5 text-xs font-semibold ${met ? 'text-green-700' : 'text-red-500'}`}
                                      key={rule.key}
                                    >
                                      {met ? (
                                        <Check
                                          size={14}
                                          className="shrink-0 text-green-600"
                                        />
                                      ) : (
                                        <X
                                          size={14}
                                          className="shrink-0 text-red-500"
                                        />
                                      )}
                                      {rule.label}
                                    </li>
                                  );
                                })}
                              </ul>
                              <p
                                className={`text-xs font-bold ${passwordStrength ? 'text-green-700' : 'text-red-500'}`}
                              >
                                {passwordStrength
                                  ? 'Senha forte'
                                  : 'Senha fraca'}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                      {isCreatingUser && (
                        <>
                          <label className="flex flex-col gap-1">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                              Senha
                            </span>
                            <PasswordInput
                              ariaLabel="Senha do usuário"
                              value={userDraft.password}
                              onChange={(value) =>
                                updateUserDraft('password', value)
                              }
                            />
                            {userErrors.password && (
                              <span className="text-xs font-bold italic text-red-600">
                                {userErrors.password}
                              </span>
                            )}
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                              Confirmar senha
                            </span>
                            <PasswordInput
                              ariaLabel="Confirmar senha do usuário"
                              invalid={confirmMismatch}
                              value={userDraft.confirmPassword}
                              onChange={(value) =>
                                updateUserDraft('confirmPassword', value)
                              }
                            />
                            {(userErrors.confirmPassword ||
                              confirmMismatch) && (
                              <span className="text-xs font-bold italic text-red-600">
                                As senhas não coincidem.
                              </span>
                            )}
                          </label>
                          <div className="flex flex-col gap-1.5 md:col-span-2">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                              Requisitos de senha forte
                            </span>
                            <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                              {PASSWORD_RULES.map((rule) => {
                                const met = rule.test(userDraft.password);
                                return (
                                  <li
                                    className={`flex items-center gap-1.5 text-xs font-semibold ${
                                      met ? 'text-green-700' : 'text-red-500'
                                    }`}
                                    key={rule.key}
                                  >
                                    {met ? (
                                      <Check
                                        size={14}
                                        className="shrink-0 text-green-600"
                                      />
                                    ) : (
                                      <X
                                        size={14}
                                        className="shrink-0 text-red-500"
                                      />
                                    )}
                                    {rule.label}
                                  </li>
                                );
                              })}
                            </ul>
                            <p
                              className={`text-xs font-bold ${
                                passwordStrength
                                  ? 'text-green-700'
                                  : 'text-red-500'
                              }`}
                            >
                              {passwordStrength ? 'Senha forte' : 'Senha fraca'}
                            </p>
                          </div>
                        </>
                      )}
                      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <input
                          aria-label="Usuário bloqueado"
                          checked={userDraft.blocked}
                          className="accent-[#EE7C2C]"
                          onChange={(event) =>
                            updateUserDraft('blocked', event.target.checked)
                          }
                          type="checkbox"
                        />
                        Usuário bloqueado
                      </label>
                    </div>

                    <div className="border-t border-slate-200 p-4">
                      <h3 className="text-base font-bold text-slate-900">
                        Grupos
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        Atribua os grupos de acesso deste usuário no aplicativo
                        da Portaria.
                      </p>
                      <div className="mt-4 grid grid-cols-1 gap-2">
                        {roles.map((role) => (
                          <label
                            className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 p-3 text-sm transition-colors hover:bg-white"
                            key={role.id}
                          >
                            <input
                              aria-label={`Atribuir grupo ${role.name}`}
                              checked={selectedRoleIds.includes(role.id)}
                              className="accent-[#EE7C2C]"
                              onChange={() => toggleRole(role.id)}
                              type="checkbox"
                            />
                            <span className="font-semibold text-slate-800">
                              {role.name}
                            </span>
                            <span className="font-mono text-[11px] font-semibold text-slate-500">
                              {role.code}
                            </span>
                          </label>
                        ))}
                        {!roles.length && (
                          <p className="rounded border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">
                            Nenhum grupo cadastrado. Cadastre grupos na aba
                            Grupos.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="flex min-h-[calc(100vh-205px)] flex-1 overflow-hidden">
              <aside className="flex w-72 shrink-0 flex-col border-r border-slate-200 bg-white">
                <div className="border-b border-slate-200 p-3">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-base font-bold text-slate-900">
                      Grupos
                    </h2>
                    <button
                      aria-label="Novo grupo de acesso"
                      className="flex h-7 w-7 items-center justify-center rounded bg-[#EE7C2C] text-white"
                      onClick={startNewRole}
                      type="button"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <div className="relative">
                    <Search
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                      size={15}
                    />
                    <input
                      aria-label="Filtrar grupos"
                      className="w-full rounded border border-slate-300 py-1.5 pl-8 pr-2 text-sm outline-none focus:border-[#EE7C2C]"
                      placeholder="Filtrar..."
                      value={roleSearch}
                      onChange={(event) => setRoleSearch(event.target.value)}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {visibleRoles.map((role) => (
                    <button
                      aria-pressed={role.id === selectedRoleId}
                      className={`relative block w-full border-b border-slate-200 p-3 text-left transition-colors ${
                        role.id === selectedRoleId
                          ? 'bg-orange-50'
                          : 'hover:bg-slate-50'
                      }`}
                      key={role.id}
                      onClick={() => selectRole(role)}
                      type="button"
                    >
                      {role.id === selectedRoleId && (
                        <span className="absolute bottom-0 left-0 top-0 w-1 bg-[#EE7C2C]" />
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <strong className="truncate text-sm text-slate-800">
                          {role.name}
                        </strong>
                        <span
                          className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${
                            role.active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {role.active ? 'ATIVO' : 'INATIVO'}
                        </span>
                      </div>
                      <span className="mt-0.5 block truncate font-mono text-[11px] font-semibold text-slate-500">
                        {role.code}
                      </span>
                    </button>
                  ))}
                  {!visibleRoles.length && (
                    <p className="p-4 text-center text-xs text-slate-500">
                      Nenhum grupo encontrado.
                    </p>
                  )}
                </div>
              </aside>

              <section className="min-w-0 flex-1 overflow-y-auto p-6">
                {!isCreatingRole && !selectedRoleId ? (
                  <div className="flex h-full items-center justify-center">
                    <p className="rounded border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500">
                      Selecione um grupo para editar ou clique em + para
                      cadastrar um novo.
                    </p>
                  </div>
                ) : (
                  <div className="mx-auto max-w-4xl rounded border border-slate-200 bg-white shadow-sm">
                    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">
                          {isCreatingRole ? 'Novo grupo' : 'Editar grupo'}
                        </h2>
                        <p className="mt-1 text-sm font-semibold text-slate-500">
                          Grupos atribuídos aos usuários do aplicativo da
                          Portaria.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isCreatingRole && selectedRoleId && (
                          <Button
                            disabled={isSavingRole}
                            mood="light"
                            onClick={handleDeleteRole}
                            size="small"
                            type="button"
                          >
                            <Trash2 size={16} /> Excluir
                          </Button>
                        )}
                        <Button
                          disabled={isSavingRole}
                          mood="primary"
                          onClick={saveRole}
                          size="small"
                          type="button"
                        >
                          <Check size={16} />{' '}
                          {isSavingRole ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </div>
                    </header>
                    <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
                      <label className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Nome
                        </span>
                        <input
                          aria-label="Nome do grupo"
                          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#EE7C2C]"
                          maxLength={100}
                          value={roleDraft.name}
                          onChange={(event) => {
                            const name = sanitizeName(event.target.value);
                            updateRoleDraft('name', name);
                            updateRoleDraft('code', normalizeCode(name));
                          }}
                        />
                        {roleErrors.name && (
                          <span className="text-xs font-bold italic text-red-600">
                            {roleErrors.name}
                          </span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Código
                        </span>
                        <input
                          aria-label="Código do grupo"
                          className="rounded border border-slate-300 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-[#EE7C2C]"
                          maxLength={50}
                          value={roleDraft.code}
                          onChange={(event) =>
                            updateRoleDraft(
                              'code',
                              normalizeCode(event.target.value)
                            )
                          }
                        />
                        {roleErrors.code && (
                          <span className="text-xs font-bold italic text-red-600">
                            {roleErrors.code}
                          </span>
                        )}
                      </label>
                      <label className="flex flex-col gap-1 md:col-span-2">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                          Descrição
                        </span>
                        <textarea
                          aria-label="Descrição do grupo"
                          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#EE7C2C]"
                          maxLength={255}
                          rows={2}
                          value={roleDraft.description ?? ''}
                          onChange={(event) =>
                            updateRoleDraft('description', event.target.value)
                          }
                        />
                      </label>
                      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <input
                          aria-label="Grupo ativo"
                          checked={roleDraft.active}
                          className="accent-[#EE7C2C]"
                          onChange={(event) =>
                            updateRoleDraft('active', event.target.checked)
                          }
                          type="checkbox"
                        />
                        Grupo ativo
                      </label>
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}
        </main>
      </S.MainPanel>
    </Scaffold>
  );
};
